/**
 * InsufficientSparksModal
 *
 * Hard-stop modal shown when a node-execute request returns HTTP 402
 * `insufficient_sparks`. Replaces the previous chat-side
 * `__insufficient_funds__` question for User-path clicks — the chat
 * was easy to miss and made users think the Generate button was
 * broken (they'd click 6 times → 6 cancel toasts).
 *
 * Informational only. There is no self-serve top-up (T276405916) — credits
 * are added by an operator via /admin/topup — so the modal explains the gap
 * and directs the user to request more, with a single dismiss action.
 *
 * The modal is mounted at the workspace level (`WorkspacePageV2`) and
 * driven by a context-provided `showInsufficientSparks(detail)` setter
 * that node-level Generate buttons call from their executeNode catch
 * blocks.
 */

import { Trans, useTranslation } from 'react-i18next'
import { useLocaleFormat } from '../../i18n/useLocaleFormat'
import { useSparks } from '../../api/hooks'
import { useOptionalProjectId } from '../../contexts/ProjectIdContext'
import Modal from './Modal'
import { Zap } from 'lucide-react'
import { projectOwnerProps, trackInteraction } from '../../analytics/interactionAnalytics'
import { classifyBlockedAction } from '../../contexts/InsufficientSparksContext'

interface InsufficientSparksModalProps {
  isOpen: boolean
  onClose: () => void
  /** Current spark balance reported by the backend at the moment the
   *  gate tripped. Always >= 0. */
  balance: number
  /** Sparks required for the run that was blocked. */
  required: number
  /** Node type of the blocked run (analytics only). */
  nodeType?: string
}

export default function InsufficientSparksModal({
  isOpen,
  onClose,
  balance,
  required,
  nodeType,
}: InsufficientSparksModalProps) {
  const { t } = useTranslation('shared')
  const { formatNumber } = useLocaleFormat()
  const projectId = useOptionalProjectId()
  const { data: wallet } = useSparks(projectId)
  const gap = Math.max(0, required - balance)

  // WHY THE REASON IS DERIVED HERE RATHER THAN CARRIED IN.
  //
  // The 402 reaches this modal through four hops (client error -> grace-period
  // record in localStorage -> per-node effect -> context), and every node type
  // has its own copy of the last hop. Threading a field through all of them to
  // reach one component that can already ask is a lot of surface for no extra
  // truth. The same numbers come from /sparks, which this modal can read.
  //
  // Capped means: the member's own remaining allowance cannot cover the run
  // while the wallet itself could. The two failures need opposite remedies, so
  // conflating them is worse than saying nothing -- "ask an operator for more
  // credits" sends someone to the wrong person when the team pool is full.
  const allowanceRemaining = wallet?.allowance_remaining ?? null
  const pool = wallet?.pool ?? null
  const cappedByAllowance =
    allowanceRemaining !== null && pool !== null && allowanceRemaining < required && pool >= required

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t(cappedByAllowance ? 'insufficientSparksModal.allowanceTitle' : 'insufficientSparksModal.title')} size="sm">
      <div className="flex flex-col gap-4" data-testid="insufficient-sparks-modal">
        <div className="flex items-start gap-3">
          <span aria-hidden className="mt-0.5 inline-flex h-6 w-6 items-center justify-center text-warning">
            <Zap className="h-5 w-5" />
          </span>
          <div className="flex-1">
            {cappedByAllowance ? (
              <>
                <p className="text-sm text-foreground" data-testid="insufficient-sparks-allowance">
                  <Trans
                    i18nKey="insufficientSparksModal.allowanceExhausted"
                    t={t}
                    values={{
                      required: formatNumber(required),
                      remaining: formatNumber(allowanceRemaining ?? 0),
                    }}
                    components={{ bold: <strong className="tabular-nums" /> }}
                  />
                </p>
                <p className="mt-2 text-xs font-medium text-text-muted">
                  {t('insufficientSparksModal.allowanceAskAdmin')}
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  <Trans
                    i18nKey="insufficientSparksModal.needCredits"
                    t={t}
                    values={{ required: formatNumber(required), balance: formatNumber(balance) }}
                    components={{ bold: <strong className="tabular-nums" /> }}
                  />
                </p>
                <p className="mt-2 text-xs font-medium text-text-muted tabular-nums">
                  {t('insufficientSparksModal.askOperator', { count: gap, formattedCount: formatNumber(gap) })}
                </p>
              </>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={() => {
              trackInteraction('canvas_insufficient_credits_modal_action', {
                action: 'close',
                block_reason: cappedByAllowance ? 'allowance_cap' : 'insufficient_balance',
                blocked_action: classifyBlockedAction(nodeType),
                project_id: projectId ?? undefined,
                ...projectOwnerProps(projectId),
              })
              onClose()
            }}
            className="px-3 py-1.5 text-sm font-medium rounded border bg-primary text-primary-foreground border-primary hover:bg-primary/90 transition-colors"
            data-testid="insufficient-sparks-cancel"
          >
            {t('insufficientSparksModal.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
